import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { Dropdown, DropdownProps, Icon, Input } from "semantic-ui-react";
import styled from "styled-components";

/**
 * A drop-in replacement for semantic-ui-react's `Dropdown` that only mounts the
 * options currently visible on screen.
 *
 * `onChange` receives `(event, { ...props, value })` so the `data.options`
 * lookups in the panel editors keep working.
 */

export type VirtualizedDropdownOption = {
  key?: string | number;
  text: React.ReactNode;
  value: string | number;
  [extra: string]: any;
};

export type VirtualizedDropdownValue = string | number | Array<string | number>;

export type VirtualizedDropdownProps = {
  options: VirtualizedDropdownOption[];
  value?: VirtualizedDropdownValue;
  defaultValue?: VirtualizedDropdownValue;
  onChange?: (event: React.SyntheticEvent, data: any) => void;
  onFocus?: (event?: React.SyntheticEvent) => void;
  multiple?: boolean;
  search?: boolean;
  selection?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  placeholder?: string;
  text?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  elementid?: string;
  [extra: string]: any;
};

const ITEM_HEIGHT = 38;
const MAX_LIST_HEIGHT = 260;
const MENU_VERTICAL_MARGIN = 4;
const SEARCH_BOX_HEIGHT = 54;

const Trigger = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-height: 38px;
  padding-right: 32px !important;
  cursor: pointer;
  /* Fallback for a trigger rendered outside the .dark wrapper. */
  background: transparent;
  color: ${({ theme }) => theme.colors["foreground-color"]};
  border: 1px solid ${({ theme }) => theme.colors["input-unselected-border"]};

  &:hover {
    border-color: ${({ theme }) => theme.colors["inputs-border"]};
  }

  &.disabled {
    cursor: default;
    pointer-events: none;
    opacity: 0.45;
  }
`;

const TriggerText = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.default {
    color: ${({ theme }) => theme.colors["search_input-placeholder-color"]};
  }
`;

const TriggerIcon = styled(Icon)`
  position: absolute !important;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  margin: 0 !important;
  opacity: 0.7;
`;

// Portalled to document.body, outside the `.dark` wrapper the app hangs its
// semantic overrides off, so every colour has to come from a theme token.
const Menu = styled.div`
  position: fixed;
  z-index: 3000;
  background: ${({ theme }) => theme.colors["dropdown-menu"]};
  color: ${({ theme }) => theme.colors["foreground-color"]};
  border: 1px solid ${({ theme }) => theme.colors["dropdown-border"]};
  border-radius: 4px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const SearchWrapper = styled.div`
  padding: 4px;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors["dropdown-divider-color"]};

  /* Mirrors the global .ui.input rules, which stop at the portal boundary. */
  .ui.input {
    width: 100%;
    color: ${({ theme }) => theme.colors["foreground-color"]};

    > input {
      background: ${({ theme }) => theme.colors["background-color"]};
      color: ${({ theme }) => theme.colors["foreground-color"]};
      border: 1px solid
        ${({ theme }) => theme.colors["input-unselected-border"]};
      border-radius: 4px;
    }

    > input::placeholder {
      color: ${({ theme }) => theme.colors["search_input-placeholder-color"]};
    }

    > input:focus {
      border-color: ${({ theme }) => theme.colors["inputs-border"]};
    }

    > i.icon {
      color: ${({ theme }) => theme.colors["foreground-color"]};
      opacity: 0.7;
    }
  }
`;

const Item = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "highlighted" && prop !== "selected",
})<{ highlighted?: boolean; selected?: boolean }>`
  display: flex;
  align-items: center;
  height: ${ITEM_HEIGHT}px;
  padding: 0 14px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors["foreground-color"]};
  font-weight: ${({ selected }) => (selected ? 700 : 400)};
  background: ${({ theme, highlighted }) =>
    highlighted
      ? theme.colors["dropdown-menu-selected-items-background-color"]
      : "transparent"};

  &:hover {
    background: ${({ theme }) =>
      theme.colors["dropdown-menu-selected-items-background-color"]};
  }
`;

const ItemText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyMessage = styled.div`
  padding: 12px 14px;
  color: ${({ theme }) => theme.colors["foreground-color"]};
  opacity: 0.7;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 3px 8px;
  margin: 2px 0;
  border-radius: 4px;
  font-size: 0.85em;
  background: ${({ theme }) => theme.colors["input-label-filter-bg"]};
  color: ${({ theme }) => theme.colors["input-label-filter-text-color"]};
  /* input-label-filter-bg is plain white in the light palette, same as the
     container behind it, so the chip needs an outline to read at all. */
  border: 1px solid ${({ theme }) => theme.colors["dropdown-border"]};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

type MenuPosition = {
  top: number;
  left: number;
  width: number;
};

function optionSearchText(option: VirtualizedDropdownOption): string {
  return typeof option.text === "string"
    ? option.text
    : String(option.value ?? "");
}

/** Case-insensitive match, ranked exact > prefix > substring. */
function filterOptions(
  options: VirtualizedDropdownOption[],
  query: string
): VirtualizedDropdownOption[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return options;

  const exact: VirtualizedDropdownOption[] = [];
  const prefix: VirtualizedDropdownOption[] = [];
  const substring: VirtualizedDropdownOption[] = [];

  options.forEach((option) => {
    const text = optionSearchText(option).toLowerCase();
    if (text === trimmed) exact.push(option);
    else if (text.startsWith(trimmed)) prefix.push(option);
    else if (text.includes(trimmed)) substring.push(option);
  });

  return [...exact, ...prefix, ...substring];
}

export const VirtualizedDropdown: React.FC<VirtualizedDropdownProps> = (
  props
) => {
  const {
    options,
    value,
    defaultValue,
    onChange,
    onFocus,
    multiple = false,
    disabled = false,
    loading = false,
    error = false,
    placeholder,
    text,
    className,
    style,
    elementid,
  } = props;

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<VirtualizedDropdownValue>(
    () => defaultValue ?? (multiple ? [] : "")
  );
  const currentValue = isControlled ? value : internalValue;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const selectedValues: Array<string | number> = useMemo(() => {
    if (multiple) return Array.isArray(currentValue) ? currentValue : [];
    return currentValue === "" || currentValue === undefined
      ? []
      : [currentValue as string | number];
  }, [currentValue, multiple]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  // Semantic parity: in multiple mode the chips are the only place to deselect.
  const selectableOptions = useMemo(
    () =>
      multiple
        ? options.filter((option) => !selectedSet.has(option.value))
        : options,
    [multiple, options, selectedSet]
  );

  const filteredOptions = useMemo(
    () => filterOptions(selectableOptions, searchQuery),
    [selectableOptions, searchQuery]
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(option.value)),
    [options, selectedSet]
  );

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const listHeight = Math.min(
      Math.max(filteredOptions.length, 1) * ITEM_HEIGHT,
      MAX_LIST_HEIGHT
    );
    const menuHeight = listHeight + SEARCH_BOX_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight && rect.top > spaceBelow;

    setMenuPosition({
      top: openUpward
        ? rect.top - menuHeight - MENU_VERTICAL_MARGIN
        : rect.bottom + MENU_VERTICAL_MARGIN,
      left: rect.left,
      width: rect.width,
    });
  }, [filteredOptions.length]);

  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  // The menu is portalled, so it has to follow its container as that scrolls.
  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => updateMenuPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, updateMenuPosition]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(0);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  const open = () => {
    if (disabled) return;
    setSearchQuery("");
    setHighlightedIndex(0);
    setIsOpen(true);
    onFocus?.();
  };

  const emitChange = (
    event: React.SyntheticEvent,
    newValue: VirtualizedDropdownValue
  ) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.(event, { ...props, value: newValue });
  };

  const selectOption = (
    event: React.SyntheticEvent,
    option: VirtualizedDropdownOption
  ) => {
    if (multiple) {
      emitChange(event, [...selectedValues, option.value]);
      setSearchQuery("");
      setHighlightedIndex(0);
    } else {
      emitChange(event, option.value);
      close();
    }
  };

  const removeValue = (
    event: React.SyntheticEvent,
    removed: string | number
  ) => {
    event.stopPropagation();
    emitChange(
      event,
      selectedValues.filter((item) => item !== removed)
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" || event.key === "Tab") {
      close();
      return;
    }

    if (filteredOptions.length === 0) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const next =
        event.key === "ArrowDown"
          ? (highlightedIndex + 1) % filteredOptions.length
          : (highlightedIndex - 1 + filteredOptions.length) %
            filteredOptions.length;
      setHighlightedIndex(next);
      virtuosoRef.current?.scrollIntoView({ index: next });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) selectOption(event, option);
    }
  };

  const triggerLabel = () => {
    if (text !== undefined && text !== null && text !== "") return text;
    if (!multiple && selectedOptions.length > 0) return selectedOptions[0].text;
    return placeholder;
  };

  const showsPlaceholder =
    (text === undefined || text === null || text === "") &&
    selectedOptions.length === 0;

  const listHeight = Math.min(
    Math.max(filteredOptions.length, 1) * ITEM_HEIGHT,
    MAX_LIST_HEIGHT
  );

  const menu =
    isOpen && menuPosition
      ? ReactDOM.createPortal(
          <Menu
            ref={menuRef}
            // Not semantic's `ui menu` classes: portalled, so those would style
            // it as a nav menu.
            className="virtualized-dropdown-menu"
            // On the menu, not the search box: semantic's Input does not forward
            // onKeyDown to the underlying input.
            onKeyDown={handleKeyDown}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            <SearchWrapper>
              <Input
                fluid
                icon="search"
                iconPosition="left"
                placeholder="Search..."
                value={searchQuery}
                autoFocus
                onChange={(_event, data) => {
                  setSearchQuery(data.value);
                  setHighlightedIndex(0);
                }}
              />
            </SearchWrapper>

            {filteredOptions.length > 0 ? (
              <Virtuoso
                ref={virtuosoRef}
                style={{ height: listHeight }}
                totalCount={filteredOptions.length}
                fixedItemHeight={ITEM_HEIGHT}
                itemContent={(index) => {
                  const option = filteredOptions[index];
                  const label = optionSearchText(option);
                  return (
                    <Item
                      highlighted={index === highlightedIndex}
                      selected={selectedSet.has(option.value)}
                      title={label}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={(event) => selectOption(event, option)}
                    >
                      <ItemText>{option.text}</ItemText>
                    </Item>
                  );
                }}
              />
            ) : (
              <EmptyMessage>No results found.</EmptyMessage>
            )}
          </Menu>,
          document.body
        )
      : null;

  return (
    <>
      <Trigger
        ref={triggerRef}
        id={elementid}
        role="listbox"
        tabIndex={disabled ? -1 : 0}
        className={[
          "ui fluid selection dropdown",
          multiple ? "multiple" : "",
          isOpen ? "active visible" : "",
          disabled ? "disabled" : "",
          error ? "error" : "",
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          borderColor: error ? "red" : undefined,
          ...(style ?? {}),
          position: "relative",
        }}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={(event) => {
          if (isOpen) return;
          if (event.key === "Enter" || event.key === "ArrowDown") {
            event.preventDefault();
            open();
          }
        }}
      >
        {multiple && selectedOptions.length > 0 ? (
          selectedOptions.map((option) => (
            <Chip key={option.key ?? option.value}>
              <span>{option.text}</span>
              <Icon
                name="delete"
                link
                style={{ margin: 0 }}
                onClick={(event: React.MouseEvent) =>
                  removeValue(event, option.value)
                }
              />
            </Chip>
          ))
        ) : (
          <TriggerText className={showsPlaceholder ? "default text" : "text"}>
            {triggerLabel()}
          </TriggerText>
        )}
        <TriggerIcon
          name={loading ? "spinner" : isOpen ? "angle up" : "angle down"}
          loading={loading}
        />
      </Trigger>
      {menu}
    </>
  );
};

/**
 * Past this many options the windowed dropdown takes over. Below it semantic's
 * own dropdown stays in place so small pickers keep their exact behaviour, with
 * `lazyLoad` so a closed menu costs nothing.
 */
export const VIRTUALIZE_OPTIONS_THRESHOLD = 200;

export const AdaptiveDropdown = (props: DropdownProps) => {
  const options = props.options;

  if (Array.isArray(options) && options.length > VIRTUALIZE_OPTIONS_THRESHOLD) {
    return (
      <VirtualizedDropdown
        {...(props as VirtualizedDropdownProps)}
        options={options as VirtualizedDropdownOption[]}
      />
    );
  }

  return <Dropdown lazyLoad {...props} />;
};

export default VirtualizedDropdown;
